/**
 * BasePrice_Service.ts
 * SKCATALOG-CC-002 CR-5 — effective-dated base price.
 *
 * Three reads and one write. `getBasePriceHistory` is what backs the editor's
 * quiet "history" affordance; `setBasePrice` is history-preserving on the server
 * (close-and-open, never an in-place overwrite), so the caller does not have to
 * do anything special to keep the previous figure.
 */

import axiosInstance from '../../api/axiosInstance';
import {
  Catalog_Item_BasePrice_Url,
  Catalog_Item_BasePrice_History_Url,
} from '../../api/endpoints';
import type {
  BasePriceRead,
  BasePriceHistoryResponse,
  SetBasePricePayload,
  SetBasePriceResponse,
} from '../../schemas/catalog/basePrice';

/* Current base price, or the one in force at `at` — GET /items/:id/base-price */
export const getBasePrice = async (
  id: string,
  params?: { location?: string; at?: string },
): Promise<BasePriceRead> => {
  const { data } = await axiosInstance.get<BasePriceRead>(Catalog_Item_BasePrice_Url(id), { params });
  return data;
};

/* The dated ladder, newest first — GET /items/:id/base-price/history */
export const getBasePriceHistory = async (
  id: string,
  params?: { location?: string; allScopes?: '1' },
): Promise<BasePriceHistoryResponse> => {
  const { data } = await axiosInstance.get<BasePriceHistoryResponse>(
    Catalog_Item_BasePrice_History_Url(id),
    { params },
  );
  return data;
};

/* Set the price — PUT /items/:id/base-price.
   Closes the open row and opens a new one. Re-sending the SAME amount is a
   no-op (`changed: false`) and writes no history, so a save that did not touch
   the price does not manufacture a ladder entry. */
export const setBasePrice = async (
  id: string,
  payload: SetBasePricePayload,
): Promise<SetBasePriceResponse> => {
  const { data } = await axiosInstance.put<SetBasePriceResponse>(
    Catalog_Item_BasePrice_Url(id),
    payload,
  );
  return data;
};
